/** @jsxImportSource react */
import "./index.css";
import { useEffect, useRef, useState } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Link,
  useLocation,
  Navigate,
  Outlet,
} from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./queryClient";
import { WelcomeView } from "./components/WelcomeView";
import { ActasView } from "./components/ActasView";
import { Resultados2023View } from "./components/Resultados2023View";
import { CongresoActualView } from "./components/CongresoActualView";
import { RenovacionView } from "./components/RenovacionView";
import { EscrutinioEnVivoView } from "./components/EscrutinioEnVivoView";
import { SimuladorBancasView } from "./components/SimuladorBancasView";

interface Embed {
  id: string;
  title: string;
  description: string;
  cellName?: string;
  isCustom?: boolean;
}

const EMBEDS: Embed[] = [
  {
    id: "actas",
    title: "Actas",
    description: "Carga y edición de actas",
    isCustom: true,
  },
  {
    id: "resultados-2023",
    title: "Resultados 2023",
    description: "Resultados de las elecciones 2023",
    isCustom: true,
  },
  {
    id: "congreso-actual",
    title: "Congreso Actual",
    description: "Composición actual del congreso",
    isCustom: true,
  },
  {
    id: "renovacion",
    title: "Renovación",
    description: "Bancas en renovación",
    isCustom: true,
  },
  {
    id: "simulador-bancas",
    title: "Simulador",
    description: "Simulador de bancas",
    isCustom: true,
  },
  {
    id: "escrutinio-en-vivo",
    title: "Escrutinio en Vivo",
    description: "Seguimiento en vivo del escrutinio",
    isCustom: true,
  },
];

function EmbedContainer({ embed }: { embed: Embed }) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current || !embed.cellName) return;

    // Clear any existing content
    containerRef.current.innerHTML = "";

    // Create and append the script element
    const script = document.createElement("script");
    script.type = "module";
    script.src =
      "https://elecciones2025.visualizando.ar/scripts/embed.js?bypass=true";
    script.setAttribute("data-cell", embed.cellName);

    containerRef.current.appendChild(script);

    // Cleanup function to remove the script and clear container
    return () => {
      if (containerRef.current) {
        containerRef.current.innerHTML = "";
      }
    };
  }, [embed.cellName]);

  return <div ref={containerRef} className="embed-container" />;
}

function EmbedPage({ embed }: { embed: Embed }) {
  return (
    <>
      <h2 className="text-3xl font-bold mb-2">{embed.title}</h2>
      <p className="text-gray-600 mb-6">{embed.description}</p>
      <EmbedContainer key={embed.id} embed={embed} />
    </>
  );
}

function CountdownTimer() {
  const [timeUntil21, setTimeUntil21] = useState("");

  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();
      const target = new Date();
      target.setHours(21, 0, 0, 0);

      // If it's already past 21:00 today, target tomorrow at 21:00
      if (now > target) {
        target.setDate(target.getDate() + 1);
      }

      const diff = target.getTime() - now.getTime();
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      setTimeUntil21(`${hours}h ${minutes}m`);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  return (
    <div
      className="flex items-center gap-2 rounded px-3 py-1.5 text-sm shadow-sm border border-slate-200/50"
      style={{ backgroundColor: "white" }}
    >
      <svg
        className="h-4 w-4 text-slate-600"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      <span className="font-semibold text-slate-900">{timeUntil21}</span>
      <span className="text-slate-600">hasta cierre</span>
    </div>
  );
}

function AppContent() {
  const location = useLocation();
  const currentPath =
    location.pathname === "/" ? "" : location.pathname.slice(1);

  // Determine which view should be visible based on route
  const isWelcome = location.pathname === "/";
  const isActas = location.pathname === "/actas";
  const isResultados2023 = location.pathname === "/resultados-2023";
  const isCongresoActual = location.pathname === "/congreso-actual";
  const isRenovacion = location.pathname === "/renovacion";
  const isSimuladorBancas = location.pathname === "/simulador-bancas";
  const isEscrutinio = location.pathname === "/escrutinio-en-vivo";

  return (
    <div
      className="flex min-h-screen flex-col"
      style={{ backgroundColor: "#F4F8FB" }}
    >
      {/* Main Content */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {/* Render all views but hide with CSS */}
        <div
          style={{
            display: isWelcome ? "flex" : "none",
            height: "100%",
            width: "100%",
            flexDirection: "column",
          }}
        >
          <WelcomeView />
        </div>
        <div
          style={{
            display: isActas ? "flex" : "none",
            height: "100%",
            width: "100%",
            flexDirection: "column",
          }}
        >
          <ActasView />
        </div>
        <div
          style={{
            display: isResultados2023 ? "flex" : "none",
            height: "100%",
            width: "100%",
            flexDirection: "column",
          }}
        >
          <Resultados2023View />
        </div>
        <div
          style={{
            display: isCongresoActual ? "flex" : "none",
            height: "100%",
            width: "100%",
            flexDirection: "column",
          }}
        >
          <CongresoActualView />
        </div>
        <div
          style={{
            display: isRenovacion ? "flex" : "none",
            height: "100%",
            width: "100%",
            flexDirection: "column",
          }}
        >
          <RenovacionView />
        </div>
        <div
          style={{
            display: isSimuladorBancas ? "flex" : "none",
            height: "100%",
            width: "100%",
            flexDirection: "column",
          }}
        >
          <SimuladorBancasView />
        </div>
        <div
          style={{
            display: isEscrutinio ? "flex" : "none",
            height: "100%",
            width: "100%",
            flexDirection: "column",
          }}
        >
          <EscrutinioEnVivoView />
        </div>
      </div>

      {/* Bottom Navigation Footer */}
      <div
        className="shrink-0 border-t border-slate-300"
        style={{ backgroundColor: "#E5EDF3" }}
      >
        <nav className="flex items-center justify-between px-6 py-3">
          <div className="flex-1" />
          <div className="flex items-center justify-center gap-2 flex-[2]">
            {EMBEDS.map((embed) => (
              <Link
                key={embed.id}
                to={`/${embed.id}`}
                className={`flex-1 text-center rounded px-4 py-2 text-sm font-medium transition-all ${
                  currentPath === embed.id
                    ? "bg-[#777] text-white shadow-sm"
                    : "bg-white/60 text-slate-700 shadow-sm border border-slate-200/50"
                }`}
              >
                {embed.title}
              </Link>
            ))}
          </div>
          <div className="flex-1 flex justify-end">
            <CountdownTimer />
          </div>
        </nav>
      </div>
    </div>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
